import Vue from 'vue'
import VueRouter from 'vue-router'
import { auth } from '@/api/auth'
import sessionStore from '@/session/sessionStore'
import { ModuleOperation } from '@/api/operations/models/module-operation'
import CustomConfigResource from '@/api/resources/ui/CustomConfigResource'
import { ChangePasswordTypeEnum, LoginCardTypeEnum } from '@/views/auth/components/login/login-layout/login-layout.model'
import { loadLanguageAsync } from '@/shared/util/i18n.helper'
import { HelperUtils } from '@/shared/util/helper-utils'
import { StatisticalAnalysisOperation } from '@/api/operations/models/statistical-analysis-operation'

Vue.use(VueRouter)

export const router = new VueRouter({
    routes: [
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/auth/components/login/login-layout/login-layout.vue')
        },
        {
            path: '/page404',
            name: 'page404',
            component: () => import('@/components/common/page404.vue'),
            beforeEnter: async (to, from, next) => {
                await loadLanguageAsync(sessionStorage.locale, sessionStorage.idLocale)
                next()
            }
        },
        {
            path: '/change-password',
            name: 'changePassword',
            component: () => import('@/views/auth/components/login/login-layout/login-layout.vue'),
            props: {
                loginCardType: LoginCardTypeEnum.CHANGE_PASSWORD,
                changePasswordType: ChangePasswordTypeEnum.RECOVERY
            },
            beforeEnter: (to, from, next) => {
                const token = to.query?.token as string | undefined
                if (token) {
                    auth.token = token
                }
                next()
            }
        },
        {
            path: '/',
            component: () => import('@/Layout.vue'),
            beforeEnter: async (to, from, next) => {
                if (to.name !== 'login' && from.name !== 'login') {
                    const token = to.query?.accessToken as string | undefined
                    if (auth.token || token) {
                        await auth.refreshToken(true, token)
                    } else {
                        auth.logOut()
                    }
                }

                if (!auth.token) {
                    next('/login')
                } else {
                    next()
                }
            },
            children: [
                {
                    path: '',
                    name: 'home',
                    component: () => import('@/modules/dashboardModule/DashboardView.vue')
                },
                // TODO: Path for new dashboard module, pending front development
                {
                    path: 'dashboard',
                    name: 'dashboard',
                    component: () => import('@/modules/dashboard-module/dashboard-module.vue')
                },
                {
                    path: 'viewer',
                    name: 'viewerModule',
                    component: () => import('@/modules/viewerModule/ViewerModule.vue')
                },
                {
                    path: 'scientific-analysis',
                    name: 'scientific-analysis-module',
                    component: () => import('@/modules/scientific-analysis-module/scientific-analysis-module.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.SCIENTIFIC_PANEL_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    }
                },
                {
                    path: 'laboratory/:labId/patient/:patId/previous-cases',
                    name: 'patientCases',
                    component: () => import('@/components/patientPreviousCases/PatientCases.vue')
                },
                {
                    path: 'requests',
                    component: () => import('@/modules/clinicalModule/RequestsIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (HelperUtils.hasModuleAccess(ModuleOperation.PETITIONER)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'clinicalGrid',
                            component: () => import('@/modules/clinicalModule/views/grid/ClinicalGrid.vue'),
                            props: true
                        },
                        {
                            path: 'patient-grid',
                            name: 'clinicalPatientGrid',
                            component: () => import('@/modules/clinicalModule/views/patientGrid/ClinicalPatientGrid.vue'),
                            props: true
                        },
                        {
                            path: 'patient-detail/:id',
                            name: 'clinicalPatientDetail',
                            component: () => import('@/modules/clinicalModule/views/patientDetail/ClinicalPatientDetail.vue'),
                            props: true
                        },
                        {
                            path: 'new-request',
                            name: 'newRequest',
                            component: () => import('@/modules/clinicalModule/views/form/StudyRequestForm.vue'),
                            props: true
                        },
                        {
                            path: 'laboratory/:labId/request/:requestId',
                            name: 'editRequest',
                            component: () => import('@/modules/clinicalModule/views/form/StudyRequestForm.vue'),
                            props: true
                        }
                    ]
                },
                {
                    path: 'registry',
                    name: 'registryIndex',
                    component: () => import('@/modules/RegistryModule/RegistryIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.REGISTRY_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: 'requests',
                            name: 'requestGrid',
                            meta: { isGridView: true },
                            component: () => import('@/modules/RegistryModule/views/requestGrid/RegistryRequestGrid.vue')
                        },
                        {
                            path: 'requests/:id',
                            name: 'viewRequest',
                            meta: { isFormView: true },
                            component: () => import('@/modules/RegistryModule/views/viewRequest/RegistryRequestDetail.vue')
                        },
                        {
                            path: 'case',
                            name: 'caseGrid',
                            meta: { isGridView: true },
                            component: () => import('@/modules/RegistryModule/views/caseGrid/RegistryCaseGrid.vue')
                        },
                        {
                            path: 'case/new',
                            name: 'newCase',
                            meta: { isFormView: true, checkChanges: true, skipChecking: false },
                            component: () => import('@/modules/RegistryModule/views/editCase/CaseIndex.vue')
                        },
                        {
                            path: 'case/:id',
                            name: 'registryEditCase',
                            meta: { isFormView: true, checkChanges: true, skipChecking: false },
                            component: () => import('@/modules/RegistryModule/views/editCase/CaseIndex.vue')
                        }
                    ]
                },
                {
                    path: 'grossing',
                    component: () => import('@/modules/grossingModule/GrossingIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.GROSSING_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'grossingGrid',
                            component: () => import('@/modules/grossingModule/views/grid/GrossingSampleGrid.vue')
                        },
                        {
                            path: 'case/:casId/sample/:samId',
                            name: 'grossingSampleView',
                            meta: {
                                modalConfirmationStateName: 'grossingMacroModalState',
                                modalConfirmationStateStore: 'grossingModalsState',
                                modalConfirmationStateUiComponentCode: 'uicomponent.grossing.modalconfig',
                                checkChanges: true,
                                skipChecking: false
                            },
                            component: () => import('@/modules/sampleProcessingModule/views/form/SampleProcessingSampleView.vue')
                        }
                    ]
                },
                {
                    path: 'grossing-touch-view',
                    component: () => import('@/modules/grossingModule/grossing-touch-view/grossing-touch-view.vue'),
                    children: [
                        {
                            path: '',
                            name: 'GrossingTouchPendingView',
                            component: () => import('@/modules/grossingModule/grossing-touch-view/views/grossing-touch-pending-view/grossing-touch-pending-view.vue')
                        },
                        // TODO: Mockup path, it must show 'case/:casId/sample/:samId'
                        {
                            path: 'sampleViewMockup',
                            name: 'GrossingTouchSampleView',
                            component: () => import('@/modules/grossingModule/grossing-touch-view/views/grossing-touch-sample-view/grossing-touch-sample-view.vue')
                        }
                    ]
                },
                {
                    path: 'waste',
                    component: () => import('@/modules/wasteModule/WasteIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.WASTE_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'wasteGrid',
                            component: () => import('@/modules/wasteModule/views/grid/WasteSampleGrid.vue')
                        }
                    ]
                },
                {
                    path: 'shipments',
                    component: () => import('@/modules/shipments-module/shipments-module.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.SHIPMENTS_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'shipmentsGrid',
                            component: () => import('@/modules/shipments-module/components/grid/shipments-grid.vue')
                        }
                    ]
                },
                {
                    path: 'archive',
                    component: () => import('@/modules/archiveModule/ArchiveIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.ARCHIVE_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'archiveGrid',
                            component: () => import('@/modules/archiveModule/views/grid/ArchiveGrid.vue')
                        }
                    ]
                },
                {
                    path: 'audit',
                    component: () => import('@/modules/auditModule/AuditIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.AUDIT_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'auditGrid',
                            component: () => import('@/modules/auditModule/views/grid/AuditGrid.vue')
                        }
                    ]
                },
                {
                    path: 'diagnosis',
                    component: () => import('@/modules/diagnosisModule/DiagnosisIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.DIAGNOSE_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'diagnosisGrid',
                            component: () => import('@/modules/diagnosisModule/views/grid/DiagnosisCaseGrid.vue')
                        },
                        {
                            path: 'case-inform/laboratory/:labId/case/:casId',
                            name: 'diagnosisCaseInform',
                            meta: {
                                checkChanges: true,
                                skipChecking: false
                            },
                            component: () => import('@/modules/diagnosisModule/views/form/DiagnosisCaseInform.vue')
                        }
                    ]
                },
                {
                    path: 'laboratory',
                    component: () => import('@/modules/laboratoryModule/LaboratoryIndex.vue'),
                    children: [
                        {
                            path: '',
                            name: 'laboratoryModule',
                            component: () => import('@/modules/laboratoryModule/LaboratoryGrid.vue'),
                            beforeEnter: async (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.LABORATORY_ACCESS)) {
                                    const laboratoryViewMode = await CustomConfigResource.get('uicomponent.laboratory.view.mode')
                                    if (laboratoryViewMode && laboratoryViewMode.config.touchView) {
                                        next({ name: 'ContentTracking', params: to.params })
                                    } else {
                                        next()
                                    }
                                } else {
                                    next('/page404')
                                }
                            }
                        },
                        {
                            path: 'case/:casId/sample/:samId',
                            name: 'laboratorySampleView',
                            meta: {
                                modalConfirmationStateName: 'laboratoryMacroModalState',
                                modalConfirmationStateStore: 'laboratoryModalsState',
                                modalConfirmationStateUiComponentCode: 'uicomponent.laboratory.modalconfig',
                                checkChanges: true,
                                skipChecking: false
                            },
                            component: () => import('@/modules/sampleProcessingModule/views/form/SampleProcessingSampleView.vue')
                        }
                    ]
                },
                {
                    path: 'incidents',
                    component: () => import('@/modules/incidentsModule/IncidentsIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.INCIDENT_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'incidentsModule',
                            component: () => import('@/modules/incidentsModule/views/grid/IncidentsGrid.vue')
                        }
                    ]
                },
                {
                    path: 'tracking-view',
                    component: () => import('@/modules/laboratoryModule/laboratoryTracking/LaboratoryTrackingView.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.LABORATORY_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'ContentTracking',
                            component: () => import('@/modules/laboratoryModule/laboratoryTracking/ContentTracking.vue')
                        },
                        {
                            path: 'home',
                            name: 'HomeTracking',
                            component: () => import('@/modules/laboratoryModule/laboratoryTracking/HomeTracking.vue')
                        }
                    ]
                },
                {
                    path: 'billing',
                    component: () => import('@/modules/billing-module/billing-module.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.BILLING_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    children: [
                        {
                            path: '',
                            name: 'billingGrid',
                            component: () => import('@/modules/billing-module/components/grid/billing-grid.vue')
                        }
                    ]
                },
                {
                    path: 'configuration',
                    meta: {
                        name: 'ConfigurationIndex'
                    },
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasAnyConfigurationOperation()) {
                            next()
                        } else {
                            next('/page404')
                        }
                    },
                    component: () => import('@/modules/configurationModule/ConfigurationIndex.vue'),
                    children: [
                        {
                            path: '',
                            name: 'ConfigurationHome',
                            component: () => import('@/modules/configurationModule/ConfigurationHome.vue')
                        },
                        {
                            path: 'centers-and-services',
                            name: 'ConfigurationCentersAndServices',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'health-management-areas',
                                    name: 'HealthManagementArea',
                                    component: () => import('@/modules/configurationModule/detail/centersAndServices/healthManagementAreas/ConfigurationHealthManagementAreasGrid.vue')
                                },
                                {
                                    path: 'centers',
                                    name: 'Centers',
                                    component: () => import('@/modules/configurationModule/detail/centersAndServices/centers/CentersConfigurationGrid.vue')
                                },
                                {
                                    path: 'departments-types',
                                    name: 'DepartmentsTypes',
                                    component: () => import('@/modules/configurationModule/detail/centersAndServices/departmentsTypes/DepartmentsTypesConfigurationGrid.vue')
                                },
                                {
                                    path: 'departments',
                                    name: 'Departments',
                                    component: () => import('@/modules/configurationModule/detail/centersAndServices/departments/DepartmentsConfigurationGrid.vue')
                                }
                            ]
                        },
                        {
                            path: 'demography',
                            name: 'ConfigurationDemography',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'countries',
                                    name: 'DemographyCountries',
                                    component: () => import('@/modules/configurationModule/detail/demography/countries/DemographyCountriesGrid.vue')
                                },
                                {
                                    path: 'provinces',
                                    name: 'DemographyProvinces',
                                    component: () => import('@/modules/configurationModule/detail/demography/provinces/DemographyProvincesGrid.vue')
                                },
                                {
                                    path: 'municipalities',
                                    name: 'DemographyMunicipalities',
                                    component: () => import('@/modules/configurationModule/detail/demography/municipalities/DemographyMunicipalitiesGrid.vue')
                                },
                                {
                                    path: 'gender',
                                    name: 'DemographyGender',
                                    component: () => import('@/modules/configurationModule/detail/demography/gender/DemographyGenderGrid.vue')
                                }
                            ]
                        },
                        {
                            path: 'people-management',
                            name: 'ConfigurationUsers',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'roles',
                                    name: 'PeopleRoles',
                                    component: () => import('@/modules/configurationModule/detail/peopleManagement/roles/PeopleRolesGrid.vue')
                                },
                                {
                                    path: 'jobs',
                                    name: 'PeopleJobs',
                                    component: () => import('@/modules/configurationModule/detail/peopleManagement/jobs/PeopleJobsGrid.vue')
                                },
                                {
                                    path: 'petitioners',
                                    name: 'PeoplePetitioners',
                                    component: () => import('@/modules/configurationModule/detail/peopleManagement/petitioners/PeoplePetitionersGrid.vue')
                                },
                                {
                                    path: 'personnel',
                                    name: 'PeoplePersonnel',
                                    component: () => import('@/modules/configurationModule/detail/peopleManagement/personnel/ConfigurationPersonnelGrid.vue')
                                },
                                {
                                    path: 'userapp',
                                    name: 'PeopleUserapp',
                                    component: () => import('@/modules/configurationModule/detail/peopleManagement/userapp/PeopleUserappGrid.vue')
                                }
                            ]
                        },
                        {
                            path: 'cases',
                            name: 'ConfigurationCases',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'regimes',
                                    name: 'CaseRegimes',
                                    component: () => import('@/modules/configurationModule/detail/cases/regimes/CaseRegimesGrid.vue')
                                },
                                {
                                    path: 'priorities',
                                    name: 'CasePriorities',
                                    component: () => import('@/modules/configurationModule/detail/cases/priorities/CasePrioritiesGrid.vue')
                                },
                                {
                                    path: 'cases-types',
                                    name: 'CaseTypes',
                                    component: () => import('@/modules/configurationModule/detail/cases/caseTypes/CaseTypesGrid.vue')
                                },
                                {
                                    path: 'cases-insurers',
                                    name: 'CaseInsurers',
                                    component: () => import('@/modules/configurationModule/detail/cases/insurers/components/grid/insurers-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'samples',
                            name: 'ConfigurationSamples',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'samples-type',
                                    name: 'SamplesType',
                                    component: () => import('@/modules/configurationModule/detail/samples/samplesTypes/SampleTypesGrid.vue')
                                },
                                {
                                    path: 'topography',
                                    name: 'TopographicCodes',
                                    component: () => import('@/modules/configurationModule/detail/samples/topography/TopographicCodesGrid.vue')
                                },
                                {
                                    path: 'morphology',
                                    name: 'SamplesMorphology',
                                    component: () => import('@/modules/configurationModule/detail/samples/morphology/MorphologicCodeGrid.vue')
                                },
                                {
                                    path: 'procedures',
                                    name: 'SamplesProcedures',
                                    component: () => import('@/modules/configurationModule/detail/samples/procedures/ProceduresGrid.vue')
                                },
                                {
                                    path: 'most-likely-diagnoses',
                                    name: 'SamplesMostLikelyDiagnoses',
                                    component: () => import('@/modules/configurationModule/detail/samples/mostLikelyDiagnoses/MostLikelyMorphologicCodesGrid.vue')
                                },
                                {
                                    path: 'processes-types',
                                    name: 'SamplesProcessesTypes',
                                    component: () => import('@/modules/configurationModule/detail/samples/processesTypes/SampleProcessesGrid.vue')
                                }
                            ]
                        },
                        {
                            path: 'elements',
                            name: 'ConfigurationElements',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'portions',
                                    name: 'PortionsType',
                                    component: () => import('@/modules/configurationModule/detail/elements/portions/PortionsConfiguration.vue')
                                },
                                {
                                    path: 'subportions',
                                    name: 'SubportionsType',
                                    component: () => import('@/modules/configurationModule/detail/elements/subportions/SubportionsConfiguration.vue')
                                }
                            ]
                        },
                        {
                            path: 'assays',
                            name: 'ConfigurationAssays',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'assays',
                                    name: 'Assays',
                                    component: () => import('@/modules/configurationModule/detail/assays/configuration-assays/components/configuration-assays-grid/configuration-assays-grid.vue')
                                },
                                {
                                    path: 'assays-groups',
                                    name: 'AssaysGroups',
                                    component: () => import('@/modules/configurationModule/detail/assays/configuration-assays-groups/components/configuration-assays-groups-grid/configuration-assays-groups-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'incidents',
                            name: 'ConfigurationIncidents',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'incident-types',
                                    name: 'IncidentTypes',
                                    component: () => import('@/modules/configurationModule/detail/incidents/incidentsTypes/ConfigurationIncidentTypesGrid.vue')
                                },
                                {
                                    path: 'incident-origins',
                                    name: 'IncidentOrigins',
                                    component: () => import('@/modules/configurationModule/detail/incidents/incidentsOrigins/ConfigurationIncidentOriginsGrid.vue')
                                }
                            ]
                        },
                        {
                            path: 'laboratory',
                            name: 'ConfigurationLaboratory',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'calendar',
                                    name: 'ParametersCalendar',
                                    component: () => import('@/modules/configurationModule/detail/laboratory/calendar/ConfigurationCalendar.vue')
                                },
                                {
                                    path: 'identity',
                                    name: 'ParametersIdentity',
                                    component: () => import('@/modules/configurationModule/detail/laboratory/identity/LaboratoryConfiguration.vue')
                                }
                            ]
                        },
                        {
                            path: 'parameters',
                            name: 'ConfigurationParameters',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'global/group/:id?',
                                    name: 'ConfigurationParametersGlobal',
                                    props: route => ({
                                        groupId: +route.params.id,
                                        serviceInstance: async () => {
                                            const service = await import('@/modules/configurationModule/detail/parameters/services/configuration-parameters-global.service')
                                            return service.configurationParametersGlobalService
                                        }
                                    }),
                                    component: () => import('@/modules/configurationModule/detail/parameters/components/configuration-parameters-form/configuration-parameters-form.vue')
                                },
                                {
                                    path: 'local/group/:id?',
                                    name: 'ConfigurationParametersLocal',
                                    props: route => ({
                                        groupId: +route.params.id,
                                        serviceInstance: async () => {
                                            const service = await import('@/modules/configurationModule/detail/parameters/services/configuration-parameters-local.service')
                                            return service.configurationParametersLocalService
                                        }
                                    }),
                                    component: () => import('@/modules/configurationModule/detail/parameters/components/configuration-parameters-form/configuration-parameters-form.vue')
                                }
                            ]
                        },
                        {
                            path: 'templates',
                            name: 'ConfigurationTemplates',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'text-templates/:id?',
                                    name: 'textTemplates',
                                    props: route => ({
                                        id: +route.params?.id
                                    }),
                                    component: () => import('@/modules/configurationModule/detail/templates/text-templates/components/text-templates-grid/text-templates-grid.vue')
                                },
                                {
                                    path: 'synoptic-templates/:id?',
                                    name: 'synopticTemplates',
                                    props: route => ({
                                        id: +route.params?.id
                                    }),
                                    component: () => import('@/modules/configurationModule/detail/templates/synoptic-templates/components/grid/configuration-templates-synoptic-templates-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-cases',
                            name: 'ConfigurationLocalCases',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'cases-types',
                                    name: 'LocalCaseTypes',
                                    component: () => import('@/modules/configurationModule/detail/local-cases/local-case-types/components/local-case-types-grid/local-case-types-grid.vue')
                                },
                                {
                                    path: 'case-validation-situations',
                                    name: 'LocalCaseValidationSituations',
                                    component: () => import('@/modules/configurationModule/detail/local-cases/local-case-validation-situations/components/local-case-validation-situations-grid/local-case-validation-situations-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-samples',
                            name: 'ConfigurationLocalSamples',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'sample-types',
                                    name: 'LocalSampleTypes',
                                    component: () => import('@/modules/configurationModule/detail/local-samples/local-sample-types/components/local-sample-types-grid/local-sample-types-grid.vue')
                                },
                                {
                                    path: 'target-response-times',
                                    name: 'TargetResponseTime',
                                    component: () => import('@/modules/configurationModule/detail/local-samples/target-response-times/components/target-response-times-grid/target-response-times-grid.vue')
                                },
                                {
                                    path: 'topographic-subset-laboratory',
                                    name: 'TopographicSubsetLaboratory',
                                    component: () => import('@/modules/configurationModule/detail/local-samples/topographic-subset-laboratory/components/topographic-subset-laboratory-grid/topographic-subset-laboratory-grid.vue')
                                },
                                {
                                    path: 'preformatted/:id?',
                                    name: 'Preformatted',
                                    props: route => ({
                                        id: +route.params?.id
                                    }),
                                    component: () => import('@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-grid/preformatted-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-assays',
                            name: 'ConfigurationLocalAssays',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'assays',
                                    name: 'LocalAssays',
                                    component: () => import('@/modules/configurationModule/detail/local-assays/assays-laboratory/components/grid/configuration-local-assays-assays-laboratory-grid.vue')
                                },
                                {
                                    path: 'assay-groups',
                                    name: 'LocalAssayGroups',
                                    component: () => import('@/modules/configurationModule/detail/local-assays/assaygroups-laboratory/components/grid/configuration-local-assays-assaygroups-laboratory-grid.vue')
                                },
                                {
                                    path: 'assay-results',
                                    name: 'LocalAssayResults',
                                    component: () => import('@/modules/configurationModule/detail/local-assays/results/components/grid/configuration-local-assays-results-grid.vue')
                                },
                                {
                                    path: 'protocols',
                                    name: 'LocalAssayProtocol',
                                    component: () => import('@/modules/configurationModule/detail/local-assays/protocols/components/grid/configuration-local-assays-protocols-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-notifications',
                            name: 'ConfigurationLocalNotifications',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'alarms',
                                    name: 'Alarms',
                                    component: () => import('@/modules/configurationModule/detail/notifications/components/alarms-grid/alarms-grid.vue')
                                },
                                {
                                    path: 'notifications-types',
                                    name: 'LocalNotificationTypes',
                                    props: {
                                        isGlobal: false,
                                        serviceInstance: async () => {
                                            const service = await import('@/modules/configurationModule/detail/notifications/services/local-notification-services')
                                            return service.localNotificationConfigurationServices
                                        }
                                    },
                                    component: () => import('@/modules/configurationModule/detail/notifications/components/notification-types-grid/notification-types-grid.vue')
                                },
                                {
                                    path: 'custom-notifications/templates',
                                    name: 'CustomNotificationTemplates',
                                    component: () => import('@/modules/configurationModule/detail/notifications/components/notification-templates-grid/notifications-templates-grid.vue')
                                },
                                {
                                    path: 'custom-notifications/predefined-notifications',
                                    name: 'CustomNotificationPredefined',
                                    component: () => import('@/modules/configurationModule/detail/notifications/components/notification-predefined-templates-grid/notification-predefined-templates-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'global-notifications',
                            name: 'ConfigurationGlobalNotifications',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'notifications-types',
                                    name: 'GlobalNotificationTypes',
                                    props: {
                                        isGlobal: true,
                                        serviceInstance: async () => {
                                            const service = await import('@/modules/configurationModule/detail/notifications/services/global-notification-services')
                                            return service.globalNotificationConfigurationServices
                                        }
                                    },
                                    component: () => import('@/modules/configurationModule/detail/notifications/components/notification-types-grid/notification-types-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-incidents',
                            name: 'ConfigurationLocalIncidents',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'incident-types',
                                    name: 'LocalIncidentTypes',
                                    component: () => import('@/modules/configurationModule/detail/incidents/incidents-types/components/incidents-types-grid/incidents-types-grid.vue')
                                },
                                {
                                    path: 'incident-origins',
                                    name: 'LocalIncidentOrigins',
                                    component: () => import('@/modules/configurationModule/detail/incidents/incidents-origins/components/incidents-origins-grid/incidents-origins-grid.vue')
                                },
                                {
                                    path: 'incident-severity',
                                    name: 'LocalIncidentSeverity',
                                    component: () => import('@/modules/configurationModule/detail/incidents/incidents-severities/components/incidents-severities-grid/incidents-severities-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'shipments',
                            name: 'ConfigurationShipments',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'automatic-shipment-rules',
                                    name: 'AutomaticShipmentRules',
                                    props: {
                                        isManual: false,
                                        serviceInstance: async () => {
                                            const service = await import('@/modules/configurationModule/detail/shipments/shipment-rules/services/shipment-rule/shipment-rule.service')
                                            return service.shipmentRuleService
                                        }
                                    },
                                    component: () => import('@/modules/configurationModule/detail/shipments/shipment-rules/components/grid/shipment-rules-grid.vue')
                                },
                                {
                                    path: 'manual-shipment-rules',
                                    name: 'ManualShipmentRules',
                                    props: {
                                        isManual: true,
                                        serviceInstance: async () => {
                                            const service = await import('@/modules/configurationModule/detail/shipments/shipment-rules/services/manual-shipment-rule/manual-shipment-rule.service')
                                            return service.manualShipmentRuleService
                                        }
                                    },
                                    component: () => import('@/modules/configurationModule/detail/shipments/shipment-rules/components/grid/shipment-rules-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'billing',
                            name: 'ConfigurationAccounting',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'accounting/units',
                                    name: 'BillableUnits',
                                    component: () => import('@/modules/configurationModule/detail/accounting/billable/units/components/grid/configuration-accounting-billable-units-grid.vue')
                                },
                                {
                                    path: 'accounting/groups-of-concepts',
                                    name: 'BillableGroupsOfConcepts',
                                    component: () => import('@/modules/configurationModule/detail/accounting/billable/groups-of-concepts/components/grid/conceptgroups-grid.vue')
                                },
                                {
                                    path: 'accounting/concepts',
                                    name: 'BillableConcepts',
                                    component: () => import('@/modules/configurationModule/detail/accounting/billable/concepts/components/grid/billable-concept-grid.vue')
                                },
                                {
                                    path: 'accounting/customer-groups',
                                    name: 'BillableCustomerGroups',
                                    component: () => import('@/modules/configurationModule/detail/accounting/billable/customer-groups/components/customer-groups-grid/customer-groups-grid.vue')
                                },
                                {
                                    path: 'accounting/customer-types',
                                    name: 'BillableCustomerTypes',
                                    component: () => import('@/modules/configurationModule/detail/accounting/billable/customer-types/components/customer-types-grid/customer-types-grid.vue')
                                },
                                {
                                    path: 'accounting/payment-method',
                                    name: 'BillablePaymentTypes',
                                    component: () => import('@/modules/configurationModule/detail/accounting/billable/payment-method/components/payment-method-grid/payment-method-grid.vue')
                                },
                                {
                                    path: 'service-activity/units',
                                    name: 'NonBillableUnits',
                                    component: () => import('@/modules/configurationModule/detail/accounting/non-billable/units/components/units-grid/units-grid.vue')
                                },
                                {
                                    path: 'service-activity/breakdowns',
                                    name: 'NonBillableBreakdowns',
                                    component: () => import('@/modules/configurationModule/detail/accounting/non-billable/breakdowns/components/breakdowns-grid/breakdowns-grid.vue')
                                },
                                {
                                    path: 'service-activity/groups-of-concepts',
                                    name: 'NonBillableGroupsOfConcepts',
                                    component: () => import('@/modules/configurationModule/detail/accounting/non-billable/groups-of-concepts/components/groups-of-concepts-grid/groups-of-concepts-grid.vue')
                                },
                                {
                                    path: 'service-activity/concepts',
                                    name: 'NonBillableConcepts',
                                    component: () => import('@/modules/configurationModule/detail/accounting/non-billable/concepts/components/concepts-grid/concepts-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-billing',
                            name: 'ConfigurationLocalAccounting',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'accounting/clients',
                                    name: 'BillableClients',
                                    component: () => import('@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-grid/configuration-local-accounting-billable-clients-grid.vue')
                                },
                                {
                                    path: 'accounting/rates',
                                    name: 'BillableRates',
                                    component: () => import('@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-grid/configuration-local-accounting-billable-rate-grid.vue')
                                },
                                {
                                    path: 'service-activity/concepts',
                                    name: 'NonBillableLocalConcepts',
                                    component: () => import('@/modules/configurationModule/detail/local-accounting/non-billable/concepts/components/concepts-grid/concepts-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'local-archive',
                            name: 'ConfigurationLocalArchive',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'archive',
                                    name: 'ArchiveLevels',
                                    component: () => import('@/modules/configurationModule/detail/local-archive/archive-levels/components/configuration-archive-levels.vue')
                                }
                            ]
                        },
                        {
                            path: 'laboratory-module',
                            name: 'ConfigurationLaboratoryModule',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'operations',
                                    name: 'LaboratoryOperations',
                                    component: () => import('@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/components/grid/laboratory-process-grid.vue')
                                },
                                {
                                    path: 'sections',
                                    name: 'LaboratorySections',
                                    component: () => import('@/modules/configurationModule/detail/local-laboratory-module/sections/components/grid/configuration-local-assays-sections-grid.vue')
                                },
                                {
                                    path: 'subportion-state-machines/:sectionId?',
                                    name: 'SubportionStateMachines',
                                    props: route => ({
                                        sectionId: +route.params?.sectionId
                                    }),
                                    component: () => import('@/modules/configurationModule/detail/local-laboratory-module/subportion-state-machines/components/grid/configuration-subportion-state-machines-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'automation',
                            name: 'ConfigurationAutomation',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'personnel/algorithms-definition',
                                    name: 'AlgorithmsDefinition',
                                    component: () => import('@/modules/configurationModule/detail/automation/personnel/algorithms-definition/components/grid/configuration-automation-personnel-algorithms-definition-grid.vue')
                                },
                                {
                                    path: 'personnel/algorithms-selection',
                                    name: 'AlgorithmsSelection',
                                    component: () => import('@/modules/configurationModule/detail/automation/personnel/algorithms-selection/components/form/configuration-automation-personnel-algorithms-selection-form.vue')
                                },
                                {
                                    path: 'personnel/entities-association',
                                    name: 'EntitiesAssociation',
                                    component: () => import('@/modules/configurationModule/detail/automation/personnel/entities-association/components/grid/configuration-automation-personnel-entities-association-grid.vue')
                                },
                                {
                                    path: 'grossing-shelf/immediate-shelf-priority',
                                    name: 'ImmediateShelfPriority',
                                    component: () => import('@/modules/configurationModule/detail/automation/grossing-shelf/immediate-shelf-priority/components/grid/configuration-automation-grossing-shelf-immediate-shelf-priority-grid.vue')
                                },
                                {
                                    path: 'grossing-shelf/intermediate-shelf-process',
                                    name: 'IntermediateShelfProcess',
                                    component: () => import('@/modules/configurationModule/detail/automation/grossing-shelf/intermediate-shelf-process/components/grid/configuration-automation-grossing-shelf-intermediate-shelf-process-grid.vue')
                                },
                                {
                                    path: 'grossing-shelf/sample-type-and-shelf',
                                    name: 'SampleTypeAndShelf',
                                    component: () => import('@/modules/configurationModule/detail/automation/grossing-shelf/sample-type-and-shelf/components/grid/configuration-automation-grossing-shelf-sample-type-and-shelf-grid.vue')
                                },
                                {
                                    path: 'portions/portion-number-per-sample',
                                    name: 'PortionNumberPerSample',
                                    component: () => import('@/modules/configurationModule/detail/automation/portions/components/portions-grid/configuration-automation-portions-grid.vue')
                                },
                                {
                                    path: 'notifications/case',
                                    name: 'CaseNotification',
                                    component: () => import('@/modules/configurationModule/detail/automation/notifications/automation-notifications-case/components/case-automatic-notification-trigger-grid/case-automatic-notification-trigger-grid.vue')
                                },
                                {
                                    path: 'billing/service-activity',
                                    name: 'ValuableConceptAutomation',
                                    component: () => import('@/modules/configurationModule/detail/automation/valuable-concepts/components/valuable-concepts-grid/configuration-valuable-concepts-grid.vue')
                                },
                                {
                                    path: 'billing/accounting',
                                    name: 'BillableConceptAutomation',
                                    component: () => import('@/modules/configurationModule/detail/automation/billable-concepts/components/automatic-billable-concept-rules-grid/configuration-automatic-billable-concept-rules-grid.vue')
                                },
                                // TODO: Mockup next section, pending development
                                {
                                    path: 'billing/service-activity',
                                    name: 'NonBillableConceptAutomation',
                                    component: () => import('@/modules/configurationModule/detail/automation/non-billable-concepts/components/non-billable-concepts-grid/non-billable-concepts-grid.vue')
                                },
                                {
                                    path: 'assays',
                                    name: 'AssayAutomation',
                                    component: () => import('@/modules/configurationModule/detail/automation/assays/components/assays-grid/configuration-automation-assays-grid.vue')
                                },
                                {
                                    path: 'protocols',
                                    name: 'ProtocolAutomation',
                                    component: () => import('@/modules/configurationModule/detail/automation/protocols/components/grid/configuration-automation-protocols-grid.vue')
                                },
                                {
                                    path: 'preformatted',
                                    name: 'PreformattedAutomation',
                                    component: () => import('@/modules/configurationModule/detail/automation/preformatted/components/preformatted-grid/configuration-automation-preformatted-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'print',
                            name: 'ConfigurationPrint',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'mockup-section',
                                    name: 'PrintMockup',
                                    component: () => import('@/modules/configurationModule/detail/print/section/SectionConfigurationGrid.vue')
                                }
                            ]
                        },
                        {
                            path: 'vitrolink',
                            name: 'ConfigurationVitrolink',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'interoperability',
                                    name: 'Interoperability',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/interoperability/components/interoperability-grid/interoperability-grid.vue')
                                },
                                {
                                    path: 'assignments/assays',
                                    name: 'AssayAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/sample-types',
                                    name: 'SampleTypeAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/locations',
                                    name: 'LocationAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/procedures',
                                    name: 'ProcedureAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/provinces',
                                    name: 'ProvinceAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/municipalities',
                                    name: 'MunicipalityAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/countries',
                                    name: 'CountryAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                },
                                {
                                    path: 'assignments/gender',
                                    name: 'GenderAssignment',
                                    component: () => import('@/modules/configurationModule/detail/vitrolink/assignments/components/assignments-grid/assignments-grid.vue')
                                }
                            ]
                        },
                        {
                            path: 'instrumentation',
                            name: 'ConfigurationInstrumentation',
                            component: () => import('@/modules/configurationModule/detail/ConfigurationDetail.vue'),
                            children: [
                                {
                                    path: 'devices',
                                    name: 'Devices',
                                    component: () => import('@/modules/configurationModule/detail/instrumentation/devices/components/grid/configuration-instrumentation-devices-grid.vue')
                                }
                            ]
                        }
                    ]
                },
                {
                    path: 'websocket-test',
                    name: 'WebSocketTest',
                    component: () => import('@/views/WebsocketTest.vue')
                },
                {
                    path: 'controlPanel',
                    name: 'controlPanel',
                    component: () => import('@/modules/controlPanelModule/ControlPanelIndex.vue'),
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasAnyControlPanelOperation()) {
                            next()
                        } else {
                            next('/page404')
                        }
                    }
                },
                {
                    path: 'statistical-analysis',
                    component: () => import('@/modules/statistical-analysis-module/statistical-analysis-module.vue'),
                    children: [
                        {
                            path: '',
                            name: 'StatisticalAnalysisHome',
                            component: () => import('@/modules/statistical-analysis-module/components/statistical-analysis-home/statistical-analysis-home.vue')
                        },
                        {
                            path: 'general-statistics',
                            name: 'GeneralStatistics',
                            component: () => import('@/modules/statistical-analysis-module/views/general-statistics-dossier/general-statistics-dossier.vue'),
                            beforeEnter: (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(StatisticalAnalysisOperation.GENERAL_STATISTIC)) {
                                    next()
                                } else {
                                    next('/page404')
                                }
                            }
                        },
                        {
                            path: 'service-activity',
                            name: 'ServiceActivity',
                            component: () => import('@/modules/statistical-analysis-module/views/service-activity-dossier/service-activity-dossier.vue'),
                            beforeEnter: (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(StatisticalAnalysisOperation.ACTIVITY)) {
                                    next()
                                } else {
                                    next('/page404')
                                }
                            }
                        },
                        {
                            path: 'delays-and-response-time',
                            name: 'DelaysAndResponseTime',
                            component: () => import('@/modules/statistical-analysis-module/views/delays-and-response-time-dossier/delays-and-response-time-dossier.vue'),
                            beforeEnter: (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(StatisticalAnalysisOperation.DELAY_AND_RESPONSE_TIME)) {
                                    next()
                                } else {
                                    next('/page404')
                                }
                            }
                        },
                        {
                            path: 'incidents',
                            name: 'IncidentStatistics',
                            component: () => import('@/modules/statistical-analysis-module/views/incidents-dossier/incidents-dossier.vue'),
                            beforeEnter: (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(StatisticalAnalysisOperation.INCIDENT)) {
                                    next()
                                } else {
                                    next('/page404')
                                }
                            }
                        },
                        {
                            path: 'labeled-samples',
                            name: 'LabeledSamples',
                            component: () => import('@/modules/statistical-analysis-module/views/labeled-samples-dossier/labeled-samples-dossier.vue'),
                            beforeEnter: (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(StatisticalAnalysisOperation.SAMPLE_TAGS)) {
                                    next()
                                } else {
                                    next('/page404')
                                }
                            }
                        },
                        {
                            path: 'billing',
                            name: 'Billing',
                            component: () => import('@/modules/statistical-analysis-module/views/labeled-samples-dossier/labeled-samples-dossier.vue'),
                            beforeEnter: (to, from, next) => {
                                if (sessionStore.hasLabOperationOnSelectedLaboratory(StatisticalAnalysisOperation.BILLING)) {
                                    next()
                                } else {
                                    next('/page404')
                                }
                            }
                        }
                    ],
                    beforeEnter: (to, from, next) => {
                        if (sessionStore.hasLabOperationOnSelectedLaboratory(ModuleOperation.STATISTICS_PANEL_ACCESS)) {
                            next()
                        } else {
                            next('/page404')
                        }
                    }
                },
                {
                    path: 'index-monitoring',
                    component: () => import('@/modules/index-monitoring-module/index-monitoring-module.vue')
                }
            ]
        },
        { path: '*', redirect: '/page404' }
    ]
})
